import { useState, useCallback, useMemo } from "react";
import { useQuery, useMutation, useSubscription } from "@apollo/client";
import {
  LIST_START_MESSAGES,
  CREATE_START_MESSAGE,
  UPDATE_START_MESSAGE,
  DELETE_START_MESSAGE,
  ATTACH_START_MESSAGE_TO_CONVERSATION,
  DETACH_START_MESSAGE_FROM_CONVERSATION,
  ON_START_MESSAGE_UPDATED,
} from "../services/graphql/start-message-operations";
import { useAuthStore } from "../stores/auth-store";
import { useGlobalClient } from "./use-global-client";
import type {
  StartMessage,
  StartMessageFilterInput,
  CreateStartMessageInput,
  UpdateStartMessageInput,
  StartMessageStats,
} from "../types/start-message-types";

interface UseStartMessagesOptions {
  filter?: StartMessageFilterInput;
  limit?: number;
  enableSubscriptions?: boolean;
}

interface UseStartMessagesResult {
  // Data
  startMessages: StartMessage[];
  loading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  stats: StartMessageStats;

  // Actions
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  clearFilter: () => void;
  createStartMessage: (input: CreateStartMessageInput) => Promise<StartMessage>;
  updateStartMessage: (
    id: string,
    input: UpdateStartMessageInput
  ) => Promise<StartMessage>;
  deleteStartMessage: (id: string) => Promise<boolean>;
  attachToConversation: (
    messageId: string,
    conversationId: string
  ) => Promise<void>;
  detachFromConversation: (
    messageId: string,
    conversationId: string
  ) => Promise<void>;
}

export function useStartMessages(
  options: UseStartMessagesOptions = {}
): UseStartMessagesResult {
  const { filter = {}, limit = 20, enableSubscriptions = true } = options;

  // Get auth state for skip conditions
  const { user, isAuthenticated, isLoading } = useAuthStore();

  // Get global client selection (handles both super admin and regular users)
  const {
    selectedClient,
    loading: clientLoading,
    isSuperAdmin,
  } = useGlobalClient();

  // Get tenant ID from selected client or user
  const tenantId = selectedClient?.id || user?.tenantId;

  // For super admins, wait until a client is selected
  // For regular users, proceed if they have a tenantId
  const shouldSkipQuery =
    isLoading ||
    (isSuperAdmin && (!selectedClient || clientLoading)) ||
    (!isSuperAdmin && !user?.tenantId);

  // Local state
  const [nextToken, setNextToken] = useState<string | null>(null);

  // Main query
  const {
    data,
    loading,
    error: queryError,
    refetch: queryRefetch,
    fetchMore,
  } = useQuery(LIST_START_MESSAGES, {
    variables: {
      filter,
      limit,
      nextToken: null,
    },
    skip: shouldSkipQuery,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  // Subscription for real-time updates - DISABLED due to subscription issues
  // TODO: Fix subscription configuration
  useSubscription(ON_START_MESSAGE_UPDATED, {
    variables: { tenantId },
    skip: !enableSubscriptions || !isAuthenticated || !tenantId || true, // Always skip for now
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData.data?.onStartMessageUpdated) {
        // Refetch to get updated data
        queryRefetch();
      }
    },
  });

  // Subscription for usage updates - DISABLED due to subscription issues
  // TODO: Fix subscription configuration
  useSubscription(ON_START_MESSAGE_UPDATED, {
    variables: { messageId: "all" }, // Listen to all message usage updates
    skip: !enableSubscriptions || !isAuthenticated || !tenantId || true, // Always skip for now
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData.data?.onStartMessageUsageUpdated) {
        // Update the specific message in the cache
        const updatedMessage = subscriptionData.data.onStartMessageUsageUpdated;
        // The Apollo cache will automatically update the UI
        queryRefetch();
      }
    },
  });

  // Mutations
  const [createMutation] = useMutation(CREATE_START_MESSAGE);
  const [updateMutation] = useMutation(UPDATE_START_MESSAGE);
  const [deleteMutation] = useMutation(DELETE_START_MESSAGE);
  const [attachMutation] = useMutation(ATTACH_START_MESSAGE_TO_CONVERSATION);
  const [detachMutation] = useMutation(DETACH_START_MESSAGE_FROM_CONVERSATION);

  // Derived data
  const startMessages = data?.listStartMessages?.items || [];
  const total = data?.listStartMessages?.total || 0;
  const hasMore = !!data?.listStartMessages?.nextToken;
  const error = queryError?.message || null;

  // Calculate stats
  const stats = useMemo((): StartMessageStats => {
    const active = startMessages.filter((msg) => msg.isActive).length;
    const totalUsage = startMessages.reduce(
      (sum, msg) => sum + msg.usageCount,
      0
    );
    const averageLength =
      startMessages.length > 0
        ? startMessages.reduce((sum, msg) => sum + msg.content.length, 0) /
          startMessages.length
        : 0;

    return {
      total,
      active,
      totalUsage,
      averageLength: Math.round(averageLength),
    };
  }, [startMessages, total]);

  // Actions
  const refetch = useCallback(async () => {
    setNextToken(null);
    await queryRefetch();
  }, [queryRefetch]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;

    await fetchMore({
      variables: {
        nextToken: data?.listStartMessages?.nextToken,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          listStartMessages: {
            ...fetchMoreResult.listStartMessages,
            items: [
              ...prev.listStartMessages.items,
              ...fetchMoreResult.listStartMessages.items,
            ],
          },
        };
      },
    });
  }, [hasMore, loading, fetchMore, data]);

  const clearFilter = useCallback(() => {
    setNextToken(null);
    queryRefetch({
      filter: {},
      limit,
      nextToken: null,
    });
  }, [queryRefetch, limit]);

  const createStartMessage = useCallback(
    async (input: CreateStartMessageInput): Promise<StartMessage> => {
      try {
        const result = await createMutation({
          variables: { input },
          refetchQueries: [LIST_START_MESSAGES],
        });

        if (!result.data?.createStartMessage) {
          throw new Error("Failed to create start message");
        }

        return result.data.createStartMessage;
      } catch (error) {
        console.error("Error creating start message:", error);
        throw error;
      }
    },
    [createMutation]
  );

  const updateStartMessage = useCallback(
    async (
      id: string,
      input: UpdateStartMessageInput
    ): Promise<StartMessage> => {
      try {
        const result = await updateMutation({
          variables: { input: { ...input, id } },
          refetchQueries: [LIST_START_MESSAGES],
        });

        if (!result.data?.updateStartMessage) {
          throw new Error("Failed to update start message");
        }

        return result.data.updateStartMessage;
      } catch (error) {
        console.error("Error updating start message:", error);
        throw error;
      }
    },
    [updateMutation]
  );

  const deleteStartMessage = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await deleteMutation({
          variables: { id },
          refetchQueries: [LIST_START_MESSAGES],
        });

        return result.data?.deleteStartMessage || false;
      } catch (error) {
        console.error("Error deleting start message:", error);
        throw error;
      }
    },
    [deleteMutation]
  );

  const attachToConversation = useCallback(
    async (messageId: string, conversationId: string): Promise<void> => {
      try {
        await attachMutation({
          variables: { messageId, conversationId },
          refetchQueries: [LIST_START_MESSAGES],
        });
      } catch (error) {
        console.error("Error attaching start message to conversation:", error);
        throw error;
      }
    },
    [attachMutation]
  );

  const detachFromConversation = useCallback(
    async (messageId: string, conversationId: string): Promise<void> => {
      try {
        await detachMutation({
          variables: { messageId, conversationId },
          refetchQueries: [LIST_START_MESSAGES],
        });
      } catch (error) {
        console.error(
          "Error detaching start message from conversation:",
          error
        );
        throw error;
      }
    },
    [detachMutation]
  );

  return {
    // Data
    startMessages,
    loading,
    error,
    total,
    hasMore,
    stats,

    // Actions
    refetch,
    loadMore,
    clearFilter,
    createStartMessage,
    updateStartMessage,
    deleteStartMessage,
    attachToConversation,
    detachFromConversation,
  };
}
